/**
 * @module Actions.PromoOffer
 *
 */
import { Dispatch } from "react";
import { ActionType, action } from "typesafe-actions";
import { fetchGet } from "app/utils/request";
import { PromoOffer } from "app/models";
import { Currency } from "app/utils/currency";

export interface ResolvePromoOfferParams {
  code: string;
  planCode: string;
  currency?: Currency;
}

// Each phase carries the request it belongs to, so the reducer can drop an
// out-of-order response (a slow earlier resolve landing after a newer one — fast
// month↔year / currency toggles) instead of overwriting the store with a stale
// plan/price.
const pending = (request: ResolvePromoOfferParams) =>
  action("@promoOffer.resolve.pending", request);
const success = (offer: PromoOffer, request: ResolvePromoOfferParams) =>
  action("@promoOffer.resolve.success", { offer, request });
// The endpoint always answers 200 (no invalid_code); `failed` only covers a
// network/transport error. The request lets the reducer clear the loader (and
// show a retry) only for the latest request.
const failed = (request: ResolvePromoOfferParams) =>
  action("@promoOffer.resolve.failed", request);

const actionHandlers = { pending, success, failed };

export type PromoOfferAction = ActionType<typeof actionHandlers>;

export const resolvePromoOffer = (params: ResolvePromoOfferParams) => {
  return (dispatch: Dispatch<PromoOfferAction>) => {
    dispatch(pending(params));

    // Request-layer underscores the query keys (planCode -> plan_code). An empty
    // code is fine — the resolution still carries baseAmount for the plain price.
    const promise = fetchGet("promo_campaigns/preview", params, { version: 1 });
    promise.then((data) => dispatch(success(data, params)));
    promise.catch(() => dispatch(failed(params)));

    return promise;
  };
};

export const actions = {
  resolvePromoOffer,
};
